import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { LockClock as LockClockIcon, CreditCard as CreditCardIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAdminAccess } from '../../hooks/useAdminAccess';

/**
 * Billing paywall (Phase 3, client side).
 *
 * When an automated community's access has expired, block the app: all members
 * see a "paused" interstitial; the org admin gets a path to choose a plan and
 * pay. The admin is allowed through to /admin/billing so they can actually pay.
 * Server-side enforcement (enforceBillingAccess middleware) is the real gate;
 * this is the UX layer.
 */
export const BillingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useTenant();
  const { isCognitoAdmin } = useAdminAccess();

  const billing = config.billing;
  const expired = billing?.mode === 'automated' && billing?.state === 'expired';
  if (!expired) return <>{children}</>;

  const admin = isCognitoAdmin();
  // Let admins reach the billing page to pay.
  if (admin && location.pathname.startsWith('/admin/billing')) return <>{children}</>;

  const communityName = config.name || config.appName || 'This community';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 2,
      }}
    >
      <Paper sx={{ p: { xs: 3, sm: 5 }, maxWidth: 560, textAlign: 'center' }}>
        <LockClockIcon color="warning" sx={{ fontSize: 56, mb: 1 }} />
        {admin ? (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Your free trial has ended
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Choose a plan to reactivate {communityName}. Your data is safe — access is restored as
              soon as you subscribe.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<CreditCardIcon />}
              onClick={() => navigate('/admin/billing')}
            >
              Choose a plan
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Access paused
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {communityName}&apos;s access is currently paused. Please contact your community
              administrator to restore it.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BillingGate;
